'use client'
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import dynamic from "next/dynamic";
import ChatHero from "./chat-hero";
import { FlipWords } from "./flip-words";
import { PrimaryButton } from "@/components/ui/primary-button";
import { PlatformTabs } from "./platform-tabs";
import { FloatingChatHero } from "@/components/marketing/agent/floating-chat-hero";
import { useLocale, useTranslations } from "next-intl";
import { capturePH } from "@/instrumentation-client";

const UnicornScene = dynamic(() => import("unicornstudio-react/next"), { ssr: false });


export function HeroAdan() {
    const t = useTranslations('home.agent.hero');
    const tCta = useTranslations('home.agent.cta');
    const [sceneLoaded, setSceneLoaded] = useState(false);
    const [sceneError, setSceneError] = useState(false);
    const [isDesktop, setIsDesktop] = useState<boolean>(false);
    const [deferHeavyScene, setDeferHeavyScene] = useState(true);

    //GET LOCALE
    const locale = useLocale();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mediaQuery = window.matchMedia('(min-width: 768px)');
        const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
            // MediaQueryListEvent for modern browsers; MediaQueryList for initial set
            const matches = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
            setIsDesktop(matches);
        };
        handleChange(mediaQuery);
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange as (e: MediaQueryListEvent) => void);
        } else if ((mediaQuery as any).addListener) {
            (mediaQuery as any).addListener(handleChange);
        }
        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleChange as (e: MediaQueryListEvent) => void);
            } else if ((mediaQuery as any).removeListener) {
                (mediaQuery as any).removeListener(handleChange);
            }
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const timer = setTimeout(() => setDeferHeavyScene(false), 2000);
        return () => clearTimeout(timer);
    }, []);
    const handleLoad = () => {
        setSceneLoaded(true);
    };

    const handleError = (error: Error) => {
        setSceneError(true);
    };


    const words = [t('words.inbound'), t('words.outbound')];

    return (
        <div className="relative">
            {/* <div className="absolute top-0 left-0 w-full h-full z-0 opacity-80">
                <UnicornScene
                    projectId="2923SHbI7iPpED7bb7Fz"
                    width="100vw"
                    height="80vh"
                    scale={1}
                    dpi={1.5}
                    fps={60}
                    altText="Interactive 3D scene"
                    ariaLabel="Animated background scene"
                    lazyLoad={true}
                    production={true}
                    onLoad={handleLoad}
                    onError={handleError}
                />
            </div> */}

            <section className="hero px-safe md:-mt-16 overflow-hidden">

                <div className="bg-secondary-blue pt-23.5 md:pt-33 lg:pt-36 xl:pt-42.5 bg-[#dde2ee] relative">
                    <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">
                        <Image
                            src="/hero-background.webp"
                            alt=""
                            fill
                            sizes="100vw"
                            priority
                            fetchPriority="high"
                            className="object-cover"
                        />
                        {isDesktop && !sceneError && !deferHeavyScene && (
                            <div className={`hidden md:block absolute inset-0 z-[1] transition-opacity duration-1000 ease-in-out ${sceneLoaded ? 'opacity-100' : 'opacity-0'}`}>
                                <UnicornScene
                                    projectId="2923SHbI7iPpED7bb7Fz"
                                    width="100%"
                                    height="100%"
                                    scale={1}
                                    dpi={1.5}
                                    fps={60}
                                    altText="Interactive 3D scene"
                                    ariaLabel="Animated background scene"
                                    lazyLoad={true}
                                    production={true}
                                    onLoad={handleLoad}
                                    onError={handleError}
                                />
                            </div>
                        )}
                    </div>

                    <div className="relative mx-auto max-w-7xl px-5 md:px-8">
                        <div className="mx-auto max-w-144 lg:max-w-160">
                            <div className='relative z-10'>
                                <div className='relative'>
                                    <h1 className={`font-medium relative z-10 flex flex-col bg-[linear-gradient(183deg,rgba(236,241,253,0)_13.9%,rgba(236,241,253,0.3)_121.71%),linear-gradient(0deg,#2E3038,#2E3038)] bg-clip-text leading-tight text-transparent filter-[url(#inset-shadow)] md:text-[80px] lg:text-[96px] xl:text-[96px] ${locale === 'es-es' ? 'text-[37px]' : 'text-[49px]'}`}>
                                        <span className='whitespace-nowrap tracking-tight'>{t('title')}</span>
                                        <span className='whitespace-nowrap -mt-2 md:-mt-2.5 lg:-mt-3.5 tracking-tight'>{t('for')} <FlipWords words={words} className="text-theos-500" /></span>
                                    </h1>
                                    <p className='mt-4 bg-[linear-gradient(183deg,rgba(236,241,253,0)_13.9%,rgba(236,241,253,0.3)_121.71%),linear-gradient(0deg,#2E3038,#2E3038)] bg-clip-text text-lg leading-snug font-medium tracking-tight text-pretty text-transparent md:text-xl/snug lg:text-2xl/snug xl:mt-2.5 xl:text-[24px]'>{t('subtitle')}</p>
                                    <div className='mt-6 flex min-h-10 flex-col justify-center gap-5 sm:flex-row sm:justify-start lg:mt-7 lg:min-h-11 xl:mt-9 xl:gap-4'>
                                        <PrimaryButton variant="dark" size="lg" onClick={() => { try { capturePH('book_demo_clicked', { location: 'hero' }) } catch {}; window.open(`/${locale}/demo`, '_self') }}>{tCta('getStarted')}</PrimaryButton>
                                        {/* <Button
                                            asChild
                                            className='lg:h-11 w-full h-10 rounded-[6px] relative font-semibold text-white inline-flex items-center justify-center text-base leading-none blue-gradient-button sm:w-[200px] md:w-[220px] lg:w-[240px] xl:w-[260px]'
                                        >
                                            <a href="https://twitter.com/intent/follow?screen_name=jh3yy" target="_blank" rel="noreferrer noopener">Get Started</a>
                                        </Button> */}
                                        {/* <div className="flex gap-4 flex-col">
                                            <PrimaryButton variant="blue" size="lg">Get Started</PrimaryButton>
                                            <PrimaryButton variant="dark" size="lg">Learn More</PrimaryButton>
                                            <PrimaryButton variant="glass" size="lg">Try Now</PrimaryButton>
                                            <PrimaryButton variant="glass-black" size="lg">Try Now</PrimaryButton>
                                            <PrimaryButton variant="light" size="lg">Get in Touch</PrimaryButton>
                                            <PrimaryButton variant="white" size="lg">Cancel</PrimaryButton>
                                            <PrimaryButton variant="black" size="lg">Submit</PrimaryButton>
                                            <PrimaryButton variant="gradient" size="lg">Submit</PrimaryButton>
                                            <PrimaryButton variant="gradient-black" size="lg">Submit</PrimaryButton>
                                        </div> */}


                                    </div>



                                </div>


                            </div>


                            <ChatHero />


                        </div>

                    </div>


                </div>
                {/* <div className="flex justify-center w-full pt-20">

                    <div className="w-1/2 animate-fade-in-right relative">

                        <Image src="/funnel/adan-bw.png" alt="" width={1000} height={400} sizes="100vw" priority={false} className='size-full object-cover opacity-70 blur-[1px]' />
                        <Image src="/funnel/adan-flow.png" alt="" width={1000} height={400} sizes="100vw" priority={false} className='top-0 -left-5 absolute size-full object-cover z-10 animate-fade-in delay-[2000ms] opacity-0' />
                    

                    </div>

                    <div className="w-1/2 animate-fade-in-left relative">
                   


                        <Image src="/funnel/adan-bw2.png" alt="" width={1000} height={400} sizes="100vw" priority={false} className='size-full object-cover opacity-70 blur-[1px]' />
                        <Image src="/funnel/adan-flow2.png" alt="" width={1000} height={400} sizes="100vw" priority={false} className='top-5 -left-5 absolute size-full object-cover z-10 animate-fade-in delay-[2000ms] opacity-0'  />

                    </div>

                </div> */}
                <div className="bg-hero-gradient h-24 md:h-28 lg:h-36 xl:h-58.5 xl:pt-26 xl:pb-21.5"></div>

            </section>

        </div>
    )
}


